import { Mediator, app, payload } from "modular-openscriptjs";
import { api } from "../declarations";
import { routes } from "../backendRoutes";
import { VoiceOrderCallClient } from "../classes/VoiceOrderCallClient";
import { $e } from "../ojs.events";
import { eParse, sendEvent, setSearchQuery } from "../helpers";

const broker = app("broker");
const dc = app("dc");

/**
 * Wires the framework-agnostic VoiceOrderCallClient (see
 * src/classes/VoiceOrderCallClient.js) into this app's event system, the
 * same way Order.js wires order placement/cancellation/etc.
 *
 * A view/component starts or ends a call by broadcasting:
 *   broker.send($e.voiceOrderCall.needs.start, payload({}))
 *   broker.send($e.voiceOrderCall.needs.end, payload({}))
 *
 * ...and reacts to it by listening on $e.voiceOrderCall.has.*:
 *   statusChanged  { status }
 *   transcript     { text }
 *   toolCalled     { name, args }
 *   heartbeat      { billedSeconds, creditsCharged }
 *   audioLevel     { level } - 0-1, how loud the agent is right now
 *   error          { message, cause }
 *   ended          { reason, billedSeconds, creditsCharged }
 */
export class VoiceOrderCall extends Mediator {
	client = null;

	// Set while a PickLocation tool call is waiting on the user to
	// confirm a location in the map picker (see pickLocationOnMap()/
	// handleLocationAvailable() below) - null the rest of the time.
	pendingLocationRespond = null;

	$$voiceOrderCall = {
		needs: {
			start: (ed, event) => this.startCall(ed),
			end: (ed, event) => this.endCall(ed),
		},

		has: {
			statusChanged: (ed, event) => {},
			transcript: (ed, event) => {},
			toolCalled: (ed, event) => {},
			heartbeat: (ed, event) => {},
			audioLevel: (ed, event) => {},
			// A call can end because the user hung up, the model ended it,
			// or an error tore it down - in every case, credits may have
			// just been charged (see the docblock above: this event's
			// payload already carries creditsCharged), so refetch the
			// balance here rather than in endCall() alone, which only
			// covers the client-initiated path. Reuses the same event
			// AiCreditCard.js/AiCreditWallet.js already trigger on
			// mount - AiCredit.js's own getBalance() handles the
			// request/response and writes it back to uc.aiCredit.value,
			// so every reactive balance display updates on its own.
			ended: (ed, event) => {
				broker.send($e.aiCredit.needs.balance);
			},
			error: (ed, event) => {},
		},
	};

	// Reuses the app's existing generic location picker (the same one
	// DeliveryRequestForm.js drives for its own pickup/dropoff fields,
	// tagged there with meta.from "DRF_PICKUP"/"DRF_DROPOFF") - this
	// mediator plays the same role, tagged "VOICE_LOCATION_PICK", so the
	// two never cross-talk even if both happened to be open at once.
	$$location = {
		is: {
			available: (ed) => this.handleLocationAvailable(ed),
		},
	};

	/**
	 * Start a new voice ordering call, unless one is already in progress.
	 */
	async startCall() {
		if (this.client && !["idle", "ended"].includes(this.client.status)) {
			return;
		}

		// REVERTED - see the "failed to start call" investigation notes
		// below. Passing `requester: api` again: plain fetch() from the
		// Android WebView could not reach the server at all here (a
		// harder failure than the WebSocket-not-connecting bug this was
		// trying to fix), confirming the app's WebView fetch/XHR stack
		// has its own native networking limitations that CapacitorHttp
		// (real native OkHttp, not the WebView's engine) exists to work
		// around - the original reason this class's docblock preferred
		// `requester` on native. The GitHub issue this fix was based on
		// (ionic-team/capacitor#7568) turned out to be a different
		// scenario than ours on closer reading: it is specifically about
		// CapacitorHttp's *enabled: true* auto-patch mode breaking
		// Socket.IO's HTTP long-polling handshake (which goes through
		// fetch/XHR before upgrading) - this app has that auto-patch
		// disabled, and our WebSocket is a plain `new WebSocket(...)`
		// with no HTTP-polling handshake for CapacitorHttp to intercept
		// in the first place, so it was never really implicated. The
		// actual "connects the session but the socket to Google never
		// connects, only on native" cause is still open - see
		// handleLocationAvailable()-style diagnostics: check what
		// ws.onclose logs (VoiceOrderCallClient.js already logs its
		// code/reason) next time this is reproduced, since Google's
		// server supplies a specific close code that will say why.
		this.client = new VoiceOrderCallClient({
			requester: api,
			sessionsPath: routes.voiceOrderSessions,
		});

		this.client.addEventListener("status", (e) =>
			this.broadcast($e.voiceOrderCall.has.statusChanged, payload(e.detail)),
		);
		this.client.addEventListener("transcript", (e) =>
			this.broadcast($e.voiceOrderCall.has.transcript, payload(e.detail)),
		);
		this.client.addEventListener("toolcall", (e) => {
			this.broadcast($e.voiceOrderCall.has.toolCalled, payload(e.detail));
			this.performFrontendToolAction(e.detail);
		});
		this.client.addEventListener("picklocation", (e) =>
			this.pickLocationOnMap(e.detail),
		);
		this.client.addEventListener("heartbeat", (e) =>
			this.broadcast($e.voiceOrderCall.has.heartbeat, payload(e.detail)),
		);
		this.client.addEventListener("audiolevel", (e) =>
			this.broadcast($e.voiceOrderCall.has.audioLevel, payload(e.detail)),
		);
		this.client.addEventListener("error", (e) => {
			// The underlying exception (network failure, CapacitorHttp
			// error, getUserMedia rejection, etc.) was always attached
			// as `cause` but never logged anywhere - only the generic
			// user-facing `message` reached the UI. Logging it here
			// means the real reason is visible in the device console
			// (e.g. via `adb logcat` / Chrome remote debugging on
			// Android) instead of just "failed to start call" with no
			// further clue.
			console.error(
				"[VoiceOrderCall] Voice call failed to start/continue:",
				e.detail.message,
				e.detail.cause,
			);
			this.broadcast($e.voiceOrderCall.has.error, payload(e.detail));
		});
		this.client.addEventListener("ended", (e) => {
			this.broadcast($e.voiceOrderCall.has.ended, payload(e.detail));
			this.client = null;
		});

		await this.client.start();
	}

	/**
	 * End the in-progress call, if any.
	 */
	async endCall() {
		await this.client?.stop("client_ended");
	}

	/**
	 * Opens the map picker for a PickLocation tool call (see
	 * VoiceOrderCallClient._pickLocationOnMap()) and remembers `respond`
	 * so handleLocationAvailable() below can answer it once the user
	 * actually confirms something.
	 */
	pickLocationOnMap({ args, respond }) {
		// Only one pick can be in flight at a time (there's only one map
		// picker). If a new one starts before the last settled somehow,
		// fail the stale one instead of letting its promise hang forever.
		if (this.pendingLocationRespond) {
			const stale = this.pendingLocationRespond;
			this.pendingLocationRespond = null;
			stale(
				JSON.stringify({
					success: false,
					message: "Superseded by a newer location pick.",
					data: {},
					meta: {},
				}),
			);
		}

		this.pendingLocationRespond = respond;

		const location = {};
		if (args?.latitude != null && args?.longitude != null) {
			location.latitude = args.latitude;
			location.longitude = args.longitude;
		}
		if (args?.address) {
			location.address = args.address;
		}

		// Diagnostic breadcrumb - if this appears in the console but
		// AddressPickerForm's own "[AddressPickerForm] map.needs.interface"
		// breadcrumb doesn't, the event never reached the picker (a
		// Location.js/broker-level problem); if neither appears, the
		// break is upstream in VoiceOrderCallClient._pickLocationOnMap().
		console.debug(
			"[VoiceOrderCall] Firing location.is.needed for PickLocation",
			location,
		);

		sendEvent(
			$e.location.is.needed,
			payload(location, { from: "VOICE_LOCATION_PICK" }),
		);
	}

	/**
	 * Answers a pending PickLocation call once the user confirms a
	 * location in the picker. Ignores the event entirely if nothing is
	 * pending, or if it's tagged for a different caller (see the
	 * $$location block above) - this is the exact meta.from
	 * discrimination DeliveryRequestForm.js already does for its own
	 * DRF_PICKUP/DRF_DROPOFF tags.
	 */
	handleLocationAvailable(ed) {
		if (!this.pendingLocationRespond) return;

		const { meta, message } = eParse(ed);
		if (meta.from !== "VOICE_LOCATION_PICK") return;

		const respond = this.pendingLocationRespond;
		this.pendingLocationRespond = null;

		respond(
			JSON.stringify({
				success: true,
				message: "Location confirmed by the user.",
				data: {
					address: message.address,
					latitude: message.latitude,
					longitude: message.longitude,
				},
				meta: {},
			}),
		);
	}

	/**
	 * Navigates to a route, first clearing dc.searchQuery whenever that
	 * route is one of dc.searchHandlingRoutes (see src/ojs.contexts.js).
	 * Those pages read/react to dc.searchQuery on load or reactively
	 * (BusinessesPage via the $e.app.has.searchQuery broker event,
	 * BusinessProductsPage/BusinessProfilePage via a v(dc.searchQuery, ...)
	 * listener) - without an explicit clear-before-navigate, a query left
	 * over from an earlier search (e.g. the agent searching "fries") would
	 * silently keep filtering whatever the agent navigates to next, even
	 * though nobody asked for that filter. Clearing happens synchronously
	 * before router.to() so the page never renders with a stale query.
	 * Doing this once here means every navigation case below - and any
	 * future one - gets this safety automatically instead of needing to
	 * remember it per tool.
	 */
	navigateClearingSearch(router, routeName, params) {
		if (dc.searchHandlingRoutes?.includes(routeName)) {
			setSearchQuery("");
		}
		router.to(routeName, params);
	}

	/**
	 * Performs the actual navigation/event-fire for the "frontend-only"
	 * tools VoiceOrderCallClient answers locally without a backend round
	 * trip (see FRONTEND_ONLY_TOOLS there) - that class stays
	 * framework-agnostic, so this mediator (which does have the app's
	 * router/broker) is where the real side effect happens.
	 */
	performFrontendToolAction({ name, args }) {
		const router = app("router");

		switch (name) {
			case "NavigateToCart":
				router.to("user.view-cart");
				break;
			case "NavigateToOrderHistory":
				router.to("orders.index");
				break;
			case "NavigateToOrderTracking":
				if (args?.order_id) {
					router.to("order.track", { orderId: args.order_id });
				}
				break;
			case "NavigateToAiCredits":
				router.to("user.ai-credits");
				break;
			case "NavigateToCapaWallet":
				router.to("user.view-wallet");
				break;
			case "OpenProductView":
				if (args?.product_id) {
					sendEvent(
						$e.product.needs.display,
						payload({ product: { id: args.product_id } }),
					);
				}
				break;
			case "OpenSubscriptionPlansForm":
				sendEvent($e.subscription.needs.plans);
				break;
			case "NavigateToSearch":
				// Clears any stale query before navigating, then - only if
				// the agent gave us a new query - sets it after the page
				// has rendered, mirroring the delay searchPlaceholder()
				// uses in src/helpers.js for the same "wait for render"
				// reason. app.search also resets the query itself on load
				// (see src/ojs.routes.js); the explicit clear here doesn't
				// rely on that and stays correct even if that changes.
				this.navigateClearingSearch(router, "app.search");
				if (args?.query) {
					setTimeout(() => setSearchQuery(args.query), 800);
				}
				break;
			case "NavigateToBusinesses":
				this.navigateClearingSearch(router, "companies.list");
				if (args?.query) {
					setTimeout(() => setSearchQuery(args.query), 800);
				}
				break;
			case "NavigateToBusinessProfile":
				if (args?.business_id) {
					this.navigateClearingSearch(router, "business.view-profile", {
						businessId: args.business_id,
					});
				}
				break;
			case "NavigateToNotifications":
				router.to("user.notifications");
				break;
			case "NavigateToSubscriptions":
				router.to("user.subscriptions");
				break;
			case "NavigateToRewards":
				router.to("/awards");
				break;
			case "NavigateToHome":
				router.to("home");
				break;
			case "NavigateToProfile":
				// user.view-profile is one of dc.searchHandlingRoutes (see
				// ojs.contexts.js), so route through the same clear-then-
				// navigate helper every other search-handling destination
				// uses, even though this tool itself takes no search term.
				this.navigateClearingSearch(router, "user.view-profile");
				break;
			case "OpenAddressesList":
				sendEvent($e.deliveryAddress.needs.addressList);
				break;
			case "OpenAddressForm":
				sendEvent($e.deliveryAddress.needs.addressForm);
				break;
			case "CloseAddressesList":
				sendEvent($e.deliveryAddress.needs.closeAddressList);
				break;
			case "CloseProductView":
				sendEvent($e.product.display.need.close);
				break;
			case "CloseSubscriptionPlansForm":
				sendEvent($e.subscription.needs.closePlans);
				break;
			case "OpenPickupDropoffForm":
				sendEvent($e.deliveryRequest.needs.requestForm);
				break;
			case "ClosePickupDropoffForm":
				sendEvent($e.deliveryRequest.needs.closeRequestForm);
				break;
			default:
				break;
		}
	}
}
